import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const CommunityGallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [currentCategory, setCurrentCategory] = useState('all');

  const sportsPhotos = [
    'https://i.postimg.cc/XYn7dsy4/31880897-1631913770225563-5071369560595103744-n.jpg',
    'https://i.postimg.cc/Z5JKpwyZ/31891045-1631910953559178-5675087884755927040-n.jpg',
    'https://i.postimg.cc/Dw7yLxbT/31958842-1634989979917942-9213256160512049152-n.jpg',
    'https://i.postimg.cc/x1gjRLK9/490591992-1455226548827768-2370799992677521749-n.jpg',
    'https://i.postimg.cc/Z5jYc6r5/502530339-9911377378945786-1426807925230536368-n.jpg',
    'https://i.postimg.cc/qvQBL287/508294284-9910870155663175-2481625108165756777-n.jpg',
    'https://i.postimg.cc/rwgyJ5Sm/508775277-9906628859420638-700662553713069195-n.jpg',
    'https://i.postimg.cc/FHGFZ0j7/508825378-9910870185663172-7809841933266739604-n.jpg',
    'https://i.postimg.cc/nhTVkqvB/509330169-9909742385775952-1402458667780342703-n.jpg',
    'https://i.postimg.cc/Gp2LS9QH/511156221-9926193164130874-6843803085316348468-n.jpg',
    'https://i.postimg.cc/fRMTm80s/56213616-2078302842253318-8128487252792705024-n.jpg',
    'https://i.postimg.cc/Z5JKpwyb/59203919-2126631167420485-9085404703621120000-n.jpg',
    'https://i.postimg.cc/8zTPMZrT/59586052-2134023633347905-8630892499646283776-n.jpg',
    'https://i.postimg.cc/JzK7qjjf/59756378-2134022776681324-4608309813355479040-n.jpg',
    'https://i.postimg.cc/3x9rB2gM/59878896-2134023616681240-3552864889338855424-n.jpg',
    'https://i.postimg.cc/pLkVZKDb/60141294-2144360375647564-1218268525777387520-n.jpg',
    'https://i.postimg.cc/t4DR5Fhj/61538339-2163760330374235-4903358609003380736-n.jpg',
    'https://i.postimg.cc/vZX8z5Wy/65318377-2219913631425571-8669800230423625728-n.jpg'
  ];

  const clientPhotos = [
    'https://i.postimg.cc/x1gjRLK9/490591992-1455226548827768-2370799992677521749-n.jpg',
    'https://i.postimg.cc/rwgyJ5Sc/499152268-9903952519688272-3543897144750900629-n.jpg',
    'https://i.postimg.cc/L8v4Tjz6/502539048-9904466549636869-6299639268221339715-n.jpg',
    'https://i.postimg.cc/rwgyJ5Sm/508775277-9906628859420638-700662553713069195-n.jpg',
    'https://i.postimg.cc/8C5pnsHs/509430969-9911377392279118-6376714900870721719-n.jpg',
    'https://i.postimg.cc/T3Q29nrn/509358289-9905774526172738-4272463015283980708-n.jpg',
    'https://i.postimg.cc/Gp2LS9QH/511156221-9926193164130874-6843803085316348468-n.jpg',
    'https://i.postimg.cc/fRMTm80s/56213616-2078302842253318-8128487252792705024-n.jpg'
  ];

  const allPhotos = [...new Set([...sportsPhotos, ...clientPhotos])];

  const getFilteredPhotos = () => {
    if (currentCategory === 'sports') return sportsPhotos;
    if (currentCategory === 'client') return clientPhotos;
    return allPhotos;
  };

  const filteredPhotos = getFilteredPhotos();

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % filteredPhotos.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + filteredPhotos.length) % filteredPhotos.length);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Rooted in Our Community
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Snow's Tax & Financial is proud to be more than a tax and wealth management firm — we're a part of the community we serve. From sponsoring local baseball and softball teams to celebrating milestones with our clients, we believe strong relationships are built both inside and outside the office. This page highlights the people, teams, and moments that make our work meaningful.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center gap-4 mb-12 flex-wrap"
        >
          <button
            onClick={() => setCurrentCategory('all')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              currentCategory === 'all'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All Photos
          </button>
          <button
            onClick={() => setCurrentCategory('sports')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              currentCategory === 'sports'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Youth Sports Sponsorships
          </button>
          <button
            onClick={() => setCurrentCategory('client')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              currentCategory === 'client'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Client Appreciation & Giveaways
          </button>
        </motion.div>

        {/* Horizontal Scrolling Gallery */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative"
        >
          <div className="flex gap-4 overflow-x-scroll pb-4 snap-x snap-mandatory custom-scrollbar" style={{ scrollBehavior: 'smooth' }}>
            {filteredPhotos.map((photo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex-shrink-0 w-72 h-72 snap-center cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <div className="relative w-full h-full rounded-xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-300">
                  <img
                    src={photo}
                    alt={`Community moment ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg font-semibold">
                      View Full Size
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
              onClick={closeLightbox}
            >
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors z-50"
              >
                <FiX />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 text-white text-5xl hover:text-gray-300 transition-colors z-50"
              >
                <FiChevronLeft />
              </button>

              <motion.img
                key={currentImage}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                src={filteredPhotos[currentImage]}
                alt={`Community moment ${currentImage + 1}`}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white text-5xl hover:text-gray-300 transition-colors z-50"
              >
                <FiChevronRight />
              </button>

              <div className="absolute bottom-4 text-white text-lg">
                {currentImage + 1} / {filteredPhotos.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 #e5e7eb;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #e5e7eb;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #2563eb;
        }
      `}</style>
    </section>
  );
};

export default CommunityGallery;